import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JENIS_KELAMIN_ROW_BG, JENIS_KELAMIN_STYLE, statusLabel } from "@/lib/constants";
import PasienForm from "@/components/PasienForm";
import AntrianDataForm from "@/components/AntrianDataForm";
import DokumenBadges from "@/components/DokumenBadges";
import PasienDatangInfo from "@/components/PasienDatangInfo";
import Pagination from "@/components/Pagination";
import ClampedText from "@/components/ClampedText";
import CardField from "@/components/CardField";
import usePhone from "@/hooks/use-phone";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fmtSelisihWaktu } from "@/lib/format";
import { BedDouble, Clock3, AlertTriangle, Save, Search, Pencil, UserCheck, CalendarClock } from "lucide-react";

// Width of the table area at which the full layout (roomier padding, column min widths, full button labels) fits without
// horizontal scroll. Measured on the table area itself, not the screen, so collapsing the sidebar counts too.
const WIDE_TABLE_MIN = 1590;   // 1920px screen, sidebar open (the Antrian page is not capped at 1400px)
// Mirrors KAMAR_UPDATE_ROLES in backend/server.py (Update Kamar, Edit Data, Pasien Datang); Admin IPRJT only views the queue.
const CAN_UPDATE_KAMAR = ["super_admin", "case_manager", "petugas_admisi"];
const DAY_MS = 86400000;
// Same fields and matching as the Jadwal Pasien search (backend build_patient_filter): case-insensitive substring.
// "01-10-2026 21:45" that may wrap only between date and time, never inside the date.
const DateTime = ({ value }) => {
  const [d, t] = fmtDateTime(value).split(" ");
  return <><span className="whitespace-nowrap">{d}</span>{t && <> <span className="whitespace-nowrap">{t}</span></>}</>;
};

const matchesSearch = (p, q) => !q || [p.nama_pasien, p.no_rm, p.kode_pengajuan].some((v) => (v || "").toLowerCase().includes(q));

export default function AntrianPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ ambang_hari: 1, items: [] });
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState("1");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [dataTarget, setDataTarget] = useState(null);
  const [dataOpen, setDataOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);           // { patient, batal: bool } for Tandai Datang / Batalkan
  const [confirming, setConfirming] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyDatang, setOnlyDatang] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState(() => new Set());   // patient ids whose Diagnosa/Tindakan show in full
  const tableBoxRef = useRef(null);
  const [wide, setWide] = useState(true);
  const phone = usePhone();

  // Re-attached when switching between cards and table, since the table box only exists in table mode.
  useEffect(() => {
    const el = tableBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setWide(entry.contentRect.width >= WIDE_TABLE_MIN));
    ro.observe(el);
    return () => ro.disconnect();
  }, [phone]);
  // Durasi Menunggu runs in real time: re-render every minute (the finest unit it shows).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const cellX = wide ? "px-3" : "px-1.5";
  const canUpdate = CAN_UPDATE_KAMAR.includes(user.role);
  // Narrow table area: Departemen/DPJP and Tgl Operasi/LOS Pasien each share one cell and Diagnosa/Rencana Tindakan sit
  // under the patient's name ("Dx"/"Tx"), so the queue fits a 1280px screen with the sidebar open.
  const columns = ["No. RM", "Nama Pasien", ...(wide ? ["Departemen", "DPJP", "Diagnosa", "Rencana Tindakan"] : ["Departemen / DPJP"]), "Waktu Pengajuan",
    ...(wide ? ["Tgl Operasi", "LOS Pasien"] : ["Tgl Operasi / LOS Pasien"]), "Jaminan", "Dokumen", "Pasien Datang", "Durasi Menunggu", ...(canUpdate ? ["Aksi"] : [])];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/antrian");
      setData(data);
      setThreshold(String(data.ambang_hari));
      setNow(Date.now());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Durasi Menunggu = pasien datang -> now (the queue only holds patients still waiting). Only arrived patients can
  // exceed the threshold; one not yet at the hospital is scheduled, not waiting.
  const items = useMemo(() => data.items.map((p) => {
    const datang = p.pasien_datang_at ? new Date(p.pasien_datang_at).getTime() : null;
    const ms = datang === null || Number.isNaN(datang) ? null : Math.max(0, now - datang);
    return { ...p, durasiMs: ms, melebihi: ms !== null && ms > data.ambang_hari * DAY_MS };
  }), [data, now]);
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((p) => matchesSearch(p, q) && (!onlyDatang || p.durasiMs !== null)),
    [items, q, onlyDatang]);

  // The summary cards always count the whole queue, not the search result.
  const datangCount = items.filter((i) => i.durasiMs !== null).length;
  const overCount = items.filter((i) => i.melebihi).length;

  useEffect(() => { setPage(1); }, [q, onlyDatang]);
  // A kamar update can shrink the list (the patient leaves "Menunggu Kamar", shown as "Waiting List"); pull `page` back in if needed.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage((p) => Math.min(p, totalPages));
  }, [filtered.length, pageSize]);

  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const saveThreshold = async () => {
    setSavingThreshold(true);
    try {
      await api.put("/settings", { waiting_threshold_days: parseInt(threshold, 10) || 0 });
      toast.success("Ambang batas berhasil disimpan");
      fetchData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingThreshold(false);
    }
  };

  const doConfirm = async () => {
    setConfirming(true);
    const { patient, batal } = confirm;
    try {
      if (batal) {
        await api.delete(`/patients/${patient.id}/pasien-datang`);
        toast.success("Tanda pasien datang dibatalkan");
      } else {
        await api.post(`/patients/${patient.id}/pasien-datang`);
        toast.success("Pasien ditandai sudah datang");
      }
    } catch (e) {
      toast.error(apiError(e));    // e.g. already marked by a colleague, or no longer in the queue
    } finally {
      setConfirming(false);
      setConfirm(null);
      fetchData();
    }
  };

  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  // Shared by the table and the phone cards, so both show the same thing and offer the same actions.
  const DX_TX = {
    diagnosa: { title: "Diagnosa", prefix: "Dx", testId: "diagnosa" },
    rencana_tindakan: { title: "Rencana Tindakan", prefix: "Tx", testId: "tindakan" },
  };
  // One expand toggle per patient: in the table's two wide columns it opens both cells together.
  const dxTx = (p, fields, withPrefix, className) => (
    <ClampedText expanded={expanded.has(p.id)} onToggle={() => toggleExpanded(p.id)} className={className}
      toggleTestId={`dxtx-toggle-${fields.length === 1 ? DX_TX[fields[0]].testId + "-" : ""}${p.no_rm}`}
      lines={fields.map((f) => ({ text: p[f], title: DX_TX[f].title, prefix: withPrefix ? DX_TX[f].prefix : undefined,
        testId: `${DX_TX[f].testId}-${p.no_rm}` }))} />
  );

  const durasi = (p, alignEnd = false) => (p.durasiMs !== null ? (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
      p.melebihi ? "text-[#5c630f]" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
      style={p.melebihi ? { backgroundColor: "#CDDC29" } : undefined}
      title={p.melebihi ? `Melebihi ${data.ambang_hari} hari sejak pasien datang` : undefined}>
      {p.melebihi ? <AlertTriangle size={12} /> : <Clock3 size={12} />}
      {fmtSelisihWaktu(p.pasien_datang_at, new Date(now).toISOString())}
    </span>
  ) : (
    <div className={`flex flex-col gap-1 ${alignEnd ? "items-end" : "items-start"}`}>
      <span className="inline-block px-2 py-0.5 rounded-full border border-slate-300 bg-white text-[11px] font-medium text-slate-600 whitespace-nowrap">
        Belum Datang
      </span>
      <span className="text-[11px] text-slate-500">
        Rencana masuk: <span className="whitespace-nowrap">{p.tanggal_masuk_rawat_inap ? fmtDate(p.tanggal_masuk_rawat_inap) : "-"}</span>
      </span>
    </div>
  ));

  const tandaiDatangButton = (p, className) => (
    <Button size="sm" variant="outline" className={`gap-1 text-xs border-[#00B9AD]/50 text-[#007d75] hover:bg-[#00B9AD]/10 ${className}`}
      onClick={() => setConfirm({ patient: p, batal: false })} data-testid={`tandai-datang-${p.no_rm}`}>
      <UserCheck size={13} /> Datang
    </Button>
  );

  const batalDatangButton = (p, className = "") => (
    <button type="button" className={`text-[11px] text-slate-500 underline underline-offset-2 hover:text-red-600 ${className}`}
      onClick={() => setConfirm({ patient: p, batal: true })} data-testid={`batal-datang-${p.no_rm}`}>
      Batalkan
    </button>
  );

  const editDataButton = (p, label, className) => (
    <Button size="sm" variant="outline" className={`gap-1 text-xs bg-white ${className}`}
      onClick={() => { setDataTarget(p); setDataOpen(true); }}
      title="Edit Data (Jenis Kelamin, Jaminan, LOS Pasien, Dokumen)" aria-label="Edit Data" data-testid={`edit-data-${p.no_rm}`}>
      <Pencil size={13} /> {label}
    </Button>
  );

  const updateKamarButton = (p, label, className) => (
    <Button size="sm" className={`gap-1 text-xs bg-[#00B9AD] hover:bg-[#009E94] text-white ${className}`}
      onClick={() => { setEditTarget(p); setFormOpen(true); }}
      title="Update Kamar" aria-label="Update Kamar" data-testid={`update-kamar-${p.no_rm}`}>
      <BedDouble size={13} /> {label}
    </Button>
  );

  const jkBadge = (p) => (
    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${JENIS_KELAMIN_STYLE[p.jenis_kelamin] || ""}`}
      data-testid={`jk-badge-${p.no_rm}`}>
      {p.jenis_kelamin === "Laki-laki" ? "Laki-laki" : "Perempuan"}
    </span>
  );

  const publikBadge = (p) => p.sumber === "pengajuan_publik" && (
    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5"
      title={`Diajukan lewat form publik${p.nama_dokter ? ` oleh ${p.nama_dokter}` : ""}`}>
      <span className="whitespace-nowrap px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-[#CDDC29]/20 text-[#6b7413] border-[#CDDC29]/50">
        Pengajuan Publik
      </span>
      <span className="whitespace-nowrap font-mono text-[10px] text-slate-400">{p.kode_pengajuan}</span>
    </p>
  );

  // Row/card tint: over-threshold yellow wins over the JK colour; arrived patients carry a teal left edge.
  const rowTint = (p) => (p.melebihi ? "" : JENIS_KELAMIN_ROW_BG[p.jenis_kelamin] || "");
  const rowStyle = (p) => (p.melebihi ? { backgroundColor: "rgba(205,220,41,0.14)" } : undefined);
  const datangEdge = (p) => (p.durasiMs !== null ? "border-l-[#00B9AD]" : "border-l-transparent");

  const emptyState = items.length === 0 ? "Tidak ada pasien Waiting List." : "Tidak ada pasien yang cocok dengan pencarian/filter.";

  const statCard = (testId, icon, iconClass, iconStyle, value, label) => (
    <Card className="border-slate-200/80 shadow-sm" data-testid={testId}>
      <CardContent className="p-5 flex items-center gap-3.5">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${iconClass}`} style={iconStyle}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
          <p className="text-xs text-slate-500 mt-1.5">{label}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="antrian-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Antrian Waiting List</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pasien dengan status "{statusLabel("Menunggu Kamar")}". Durasi Menunggu dihitung sejak pasien ditandai datang di rumah sakit.
        </p>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${user.role === "super_admin" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {statCard("antrian-datang-card", <UserCheck size={20} />, "bg-[#00B9AD]/12 text-[#009E94]", undefined,
          datangCount, "Sudah Datang, Menunggu Kamar")}
        {statCard("antrian-terjadwal-card", <CalendarClock size={20} />, "bg-slate-100 text-slate-500", undefined,
          items.length - datangCount, "Terjadwal, Belum Datang")}
        {statCard("antrian-over-card", <AlertTriangle size={20} />, "", { backgroundColor: "rgba(205,220,41,0.25)", color: "#6b7413" },
          overCount, `Menunggu > ${data.ambang_hari} hari sejak datang`)}
        {user.role === "super_admin" && (
          <Card className="border-slate-200/80 shadow-sm" data-testid="threshold-card">
            <CardContent className="p-5">
              <label className="text-xs font-medium text-slate-500 block mb-2">Ambang Notifikasi (hari sejak pasien datang)</label>
              <div className="flex gap-2">
                <Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                  className="w-24" data-testid="threshold-input" />
                <Button size="sm" className="gap-1.5 bg-[#267B84] hover:bg-[#1e5f66] text-white"
                  onClick={saveThreshold} disabled={savingThreshold} data-testid="threshold-save-button">
                  <Save size={13} /> Simpan
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="border-slate-200/80 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-5">
          <div className="w-full sm:max-w-md">
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Cari Nama / No. RM / Kode Pengajuan</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8" placeholder="Ketik nama, No. RM, atau kode P-..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="antrian-search-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 h-10 cursor-pointer select-none">
            <Checkbox checked={onlyDatang} onCheckedChange={(v) => setOnlyDatang(Boolean(v))} data-testid="antrian-only-datang" />
            Hanya pasien datang
          </label>
        </CardContent>
      </Card>

      {phone ? (
        // Phone: one card per patient, the same data and actions as a table row; nothing scrolls sideways.
        <div className="space-y-3" data-testid="antrian-cards">
          {loading ? (
            <Card className="border-slate-200/80 shadow-sm"><CardContent className="py-14 text-center text-sm text-slate-400">Memuat data...</CardContent></Card>
          ) : filtered.length === 0 ? (
            <Card className="border-slate-200/80 shadow-sm"><CardContent className="py-14 text-center text-sm text-slate-400">
              <BedDouble className="mx-auto mb-2 text-slate-300" size={28} />
              {emptyState}
            </CardContent></Card>
          ) : pageItems.map((p) => (
            <article key={p.id} className={`rounded-xl border border-slate-200/80 border-l-4 ${datangEdge(p)} bg-white shadow-sm overflow-hidden`}
              data-testid={`antrian-row-${p.no_rm}`}>
              <div className={`p-3.5 space-y-3 ${rowTint(p)}`} style={rowStyle(p)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 break-words">{p.nama_pasien}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500">
                      <span className="font-mono text-slate-600">{p.no_rm}</span>
                      <span aria-hidden="true">·</span>
                      <span className="whitespace-nowrap">{p.umur} th</span>
                      {jkBadge(p)}
                    </p>
                    {publikBadge(p)}
                  </div>
                  <div className="shrink-0 max-w-[45%] flex flex-col items-end gap-1 text-right" data-testid={`durasi-${p.no_rm}`}>
                    {durasi(p, true)}
                    {p.melebihi && <span className="text-[11px] font-semibold text-[#5c630f]">Melebihi {data.ambang_hari} hari</span>}
                  </div>
                </div>
                {dxTx(p, ["diagnosa", "rencana_tindakan"], true)}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 border-t border-slate-900/10 pt-3">
                  <CardField label="Departemen">{p.ksm_nama || "-"}</CardField>
                  <CardField label="DPJP">{p.dpjp_nama || "-"}</CardField>
                  <CardField label="Waktu Pengajuan"><span data-testid={`waktu-pengajuan-${p.no_rm}`}><DateTime value={p.created_at} /></span></CardField>
                  <CardField label="Tgl Operasi"><span className="whitespace-nowrap">{fmtDate(p.tanggal_operasi)}</span></CardField>
                  <CardField label="LOS Pasien"><span data-testid={`los-pasien-${p.no_rm}`}>{p.los_pasien || "-"}</span></CardField>
                  <CardField label="Jaminan"><span data-testid={`jaminan-${p.no_rm}`}>{p.jaminan}{p.kelas_jaminan ? ` (Kelas ${p.kelas_jaminan})` : ""}</span></CardField>
                  <CardField label="Dokumen"><DokumenBadges patient={p} /></CardField>
                  <CardField label="Pasien Datang">
                    <div data-testid={`pasien-datang-${p.no_rm}`}>
                      {p.pasien_datang_at ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <PasienDatangInfo patient={p} wrap />
                          {canUpdate && batalDatangButton(p, "py-1.5")}
                        </div>
                      ) : <span className="text-xs text-slate-400">Belum datang</span>}
                    </div>
                  </CardField>
                </dl>
                {canUpdate && (
                  <div className="flex flex-wrap gap-2 border-t border-slate-900/10 pt-3">
                    {!p.pasien_datang_at && tandaiDatangButton(p, "h-10 flex-1 min-w-[6.5rem] bg-white")}
                    {editDataButton(p, "Edit Data", "h-10 flex-1 min-w-[6.5rem]")}
                    {updateKamarButton(p, "Update Kamar", "h-10 flex-1 min-w-[8rem]")}
                  </div>
                )}
              </div>
            </article>
          ))}
          {!loading && filtered.length > 0 && (
            <Card className="border-slate-200/80 shadow-sm overflow-hidden">
              <Pagination page={page} pageSize={pageSize} total={filtered.length}
                onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
                itemLabel="pasien" testId="antrian-pagination" />
            </Card>
          )}
        </div>
      ) : (
      <Card className="border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto" ref={tableBoxRef} data-layout={wide ? "wide" : "compact"}>
          <table className="w-full text-sm min-w-[900px]" data-testid="antrian-table">
            <thead className="bg-[#267B84] text-white">
              <tr>
                {columns.map((h) => (
                  <th key={h} className={`text-left ${cellX} py-3 text-xs font-semibold`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={columns.length} className="px-4 py-14 text-center text-slate-400">Memuat data...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length} className="px-4 py-14 text-center text-slate-400">
                  <BedDouble className="mx-auto mb-2 text-slate-300" size={28} />
                  {emptyState}
                </td></tr>
              ) : pageItems.map((p) => (
                <tr key={p.id} className={`transition-colors ${rowTint(p)}`} style={rowStyle(p)} data-testid={`antrian-row-${p.no_rm}`}>
                  {/* Arrived patients carry a teal edge, so they stand out without reordering the queue. */}
                  <td className={`${cellX} py-3 font-mono text-xs text-slate-600 whitespace-nowrap border-l-4 ${datangEdge(p)}`}>{p.no_rm}</td>
                  <td className={`${cellX} py-3 ${wide ? "min-w-[136px]" : "min-w-[118px]"}`}>
                    <p className="font-medium text-slate-800">{p.nama_pasien}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400">
                      {p.umur} th
                      {jkBadge(p)}
                    </p>
                    {publikBadge(p)}
                    {!wide && (
                      dxTx(p, ["diagnosa", "rencana_tindakan"], true, "mt-1.5 max-w-[180px]")
                    )}
                  </td>
                  {wide ? (
                    <>
                      <td className={`${cellX} py-3 text-slate-600`}>{p.ksm_nama}</td>
                      <td className={`${cellX} py-3 text-slate-600`}>{p.dpjp_nama}</td>
                      <td className={`${cellX} py-3 min-w-[140px]`}>{dxTx(p, ["diagnosa"], false, "max-w-[200px]")}</td>
                      <td className={`${cellX} py-3 min-w-[140px]`}>{dxTx(p, ["rencana_tindakan"], false, "max-w-[200px]")}</td>
                    </>
                  ) : (
                    <td className={`${cellX} py-3`} data-testid={`ksm-dpjp-${p.no_rm}`}>
                      <p className="text-slate-700">{p.ksm_nama}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{p.dpjp_nama}</p>
                    </td>
                  )}
                  <td className={`${cellX} py-3 text-slate-500`} data-testid={`waktu-pengajuan-${p.no_rm}`}><DateTime value={p.created_at} /></td>
                  {wide ? (
                    <>
                      <td className={`${cellX} py-3 text-slate-600 whitespace-nowrap`}>{fmtDate(p.tanggal_operasi)}</td>
                      <td className={`${cellX} py-3 text-slate-600`} data-testid={`los-pasien-${p.no_rm}`}>{p.los_pasien || "-"}</td>
                    </>
                  ) : (
                    <td className={`${cellX} py-3`}>
                      <p className="text-slate-700 whitespace-nowrap">{fmtDate(p.tanggal_operasi)}</p>
                      <p className="mt-0.5 text-xs text-slate-500">LOS: <span data-testid={`los-pasien-${p.no_rm}`}>{p.los_pasien || "-"}</span></p>
                    </td>
                  )}
                  <td className={`${cellX} py-3 text-slate-600`} data-testid={`jaminan-${p.no_rm}`}>
                    {p.jaminan}{p.kelas_jaminan ? ` (Kelas ${p.kelas_jaminan})` : ""}
                  </td>
                  <td className={`${cellX} py-3`}><DokumenBadges patient={p} /></td>
                  <td className={`${cellX} py-3`} data-testid={`pasien-datang-${p.no_rm}`}>
                    {p.pasien_datang_at ? (
                      <div className="flex flex-col items-start gap-1">
                        <PasienDatangInfo patient={p} wrap={!wide} />
                        {canUpdate && batalDatangButton(p)}
                      </div>
                    ) : canUpdate ? tandaiDatangButton(p, `h-8 ${wide ? "" : "px-2"}`)
                      : <span className="text-xs text-slate-400">Belum datang</span>}
                  </td>
                  <td className={`${cellX} py-3`} data-testid={`durasi-${p.no_rm}`}>{durasi(p)}</td>
                  {canUpdate && <td className={`${cellX} py-3`}>
                    <div className="flex flex-col gap-1.5">
                      {editDataButton(p, wide ? "Edit Data" : "Edit", `h-8 ${wide ? "" : "px-2"}`)}
                      {/* Short label when the table area is narrow (e.g. 1280px screen with the sidebar expanded) */}
                      {updateKamarButton(p, wide ? "Update Kamar" : "Kamar", `h-8 ${wide ? "" : "px-2"}`)}
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            itemLabel="pasien" testId="antrian-pagination" />
        )}
      </Card>
      )}

      <PasienForm open={formOpen} onOpenChange={setFormOpen} patient={editTarget} mode="kamar" onSaved={fetchData} />
      <AntrianDataForm open={dataOpen} onOpenChange={setDataOpen} patient={dataTarget} onSaved={fetchData} />

      <Dialog open={Boolean(confirm)} onOpenChange={(v) => !v && !confirming && setConfirm(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="pasien-datang-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{confirm?.batal ? "Batalkan Tanda Pasien Datang?" : "Tandai Pasien Sudah Datang?"}</DialogTitle>
            <DialogDescription className="text-xs">
              {confirm?.patient.nama_pasien} ({confirm?.patient.no_rm}).{" "}
              {confirm?.batal
                ? "Waktu kedatangan dikosongkan dan Durasi Menunggu berhenti dihitung. Tercatat di Audit Log."
                : "Durasi Menunggu mulai dihitung sejak sekarang. Waktu dan role Anda dicatat, juga di Audit Log."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={confirming} data-testid="pasien-datang-cancel">Kembali</Button>
            <Button onClick={doConfirm} disabled={confirming} data-testid="pasien-datang-confirm"
              className={confirm?.batal ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#00B9AD] hover:bg-[#009E94] text-white"}>
              {confirming ? "Memproses..." : confirm?.batal ? "Ya, Batalkan" : "Ya, Pasien Sudah Datang"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
